import React, { useEffect, useRef } from "react";
import styled, { useTheme } from "styled-components";

import i18n from "../../../../../i18n";
import { AppLangs } from "../../../../../model/shared/enum/AppLangs.enum";

const BAR_WIDTH = 3;
const BAR_GAP = 2;
const STEP = BAR_WIDTH + BAR_GAP;
/** How often a new bar is added, so the row scrolls at a readable pace */
const SAMPLE_MS = 60;
const MIN_BAR_HEIGHT = 2;

const Canvas = styled.canvas<{ $rtl: boolean }>`
  flex: 1;
  min-width: 0;
  height: 1.5rem;
  transform: ${({ $rtl }) => ($rtl ? "scaleX(-1)" : "none")};
`;

/** How loud the microphone is, as 0 to 1. Speech sits low on a linear scale,
 * so the square root lifts it into the middle of the bar instead of a sliver.
 */
const levelOf = (samples: Uint8Array) => {
  let sum = 0;

  for (const sample of samples) {
    const value = (sample - 128) / 128;
    sum += value * value;
  }

  return Math.min(1, Math.sqrt(Math.sqrt(sum / samples.length)) * 1.6);
};

type Props = {
  analyser: AnalyserNode | null;
};

/** A row of bars that rises and falls with the voice while recording, newest
 * at the end. Flat bars mean the microphone hears nothing, which is worth
 * knowing before the note is sent.
 */
export const WAVoiceLevelBars: React.FC<Props> = ({ analyser }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();
  const rtl = (i18n.language as AppLangs) === AppLangs.AR;

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");

    if (!canvas || !context || !analyser) {
      return;
    }

    const samples = new Uint8Array(analyser.fftSize);
    const levels: number[] = [];
    let frame = 0;
    let sampledAt = 0;

    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);

      if (now - sampledAt >= SAMPLE_MS) {
        sampledAt = now;
        analyser.getByteTimeDomainData(samples);
        levels.push(levelOf(samples));
      }

      const { clientWidth: width, clientHeight: height } = canvas;
      const ratio = window.devicePixelRatio || 1;

      // Sized from the element each time, so it stays sharp and fills the row
      // whatever the window does
      if (canvas.width !== width * ratio || canvas.height !== height * ratio) {
        canvas.width = width * ratio;
        canvas.height = height * ratio;
      }

      // One more than fits, so there is always a bar sliding out at the far end
      const fits = Math.ceil(width / STEP) + 1;

      if (levels.length > fits) {
        levels.splice(0, levels.length - fits);
      }

      // Each new bar pushes the row one step along. Drawing every frame part of
      // the way there turns a jump per sample into a steady scroll.
      const glide = STEP * (1 - Math.min(1, (now - sampledAt) / SAMPLE_MS));

      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);
      context.fillStyle = theme.main;
      context.beginPath();

      levels.forEach((level, index) => {
        const barHeight = Math.max(MIN_BAR_HEIGHT, level * height);
        const x = width - (levels.length - index) * STEP + glide;
        const y = (height - barHeight) / 2;

        if (context.roundRect) {
          context.roundRect(x, y, BAR_WIDTH, barHeight, BAR_WIDTH / 2);
        } else {
          context.rect(x, y, BAR_WIDTH, barHeight);
        }
      });

      context.fill();
    };

    frame = requestAnimationFrame(draw);

    return () => cancelAnimationFrame(frame);
  }, [analyser, theme]);

  return <Canvas ref={canvasRef} $rtl={rtl} aria-hidden />;
};
